import { useCallback, useEffect, useState } from "react";

export type MobileScreen = "inbox" | "reader";
export type MobileMessageMode = "sheet" | "fullscreen";

const READER_HISTORY_KEY = "mobileMailReader";

type HistoryState = {
  [READER_HISTORY_KEY]?: number;
  mobileMailMode?: MobileMessageMode;
};

export function useMobileNavigation() {
  const [screen, setScreen] = useState<MobileScreen>("inbox");
  const [selectedUid, setSelectedUid] = useState<number | null>(null);
  const [messageMode, setMessageMode] = useState<MobileMessageMode | null>(null);
  const [navOpen, setNavOpen] = useState(false);

  const applyInbox = useCallback(() => {
    setScreen("inbox");
    setSelectedUid(null);
    setMessageMode(null);
  }, []);

  const openMessage = useCallback((uid: number, _mode: MobileMessageMode = "fullscreen") => {
    setSelectedUid(uid);
    setMessageMode("fullscreen");
    setScreen("reader");
    window.history.pushState(
      { [READER_HISTORY_KEY]: uid, mobileMailMode: "fullscreen" } satisfies HistoryState,
      ""
    );
  }, []);

  /**
   * Ferme le message tout de suite (sheet / reader).
   * Remplace l’entrée historique reader pour ne pas bloquer réponse/transfert.
   */
  const backToInbox = useCallback(() => {
    const hadReader = window.history.state?.[READER_HISTORY_KEY] != null;
    applyInbox();
    if (hadReader) {
      window.history.replaceState({}, "");
    }
  }, [applyInbox]);

  useEffect(() => {
    const onPopState = () => {
      applyInbox();
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [applyInbox]);

  const openNav = useCallback(() => setNavOpen(true), []);
  const closeNav = useCallback(() => setNavOpen(false), []);

  const sheetOpen = selectedUid != null && messageMode === "sheet";
  const readerOpen = screen === "reader" && messageMode === "fullscreen";

  return {
    screen,
    selectedUid,
    messageMode,
    sheetOpen,
    readerOpen,
    navOpen,
    openMessage,
    backToInbox,
    openNav,
    closeNav,
  };
}
